import type { ConnectedStandardSolanaWallet } from "@privy-io/react-auth/solana"
import type { useSignTransaction } from "@privy-io/react-auth/solana"
import {
  type TransactionOrVersionedTransaction,
  createSolanaSigner,
  isLegacySolanaTransaction,
} from "@rarible_int/anaconda_sdk/solana"
import { PublicKey, Transaction, VersionedTransaction } from "@solana/web3.js"

export type PrivySolanaChain = "solana:mainnet" | "solana:devnet"

type SignTransactionFn = ReturnType<
  typeof useSignTransaction
>["signTransaction"]

export function createPrivySolanaSigner({
  address,
  wallet,
  chain,
  signTransaction,
}: {
  address: string
  wallet: ConnectedStandardSolanaWallet
  chain: PrivySolanaChain
  signTransaction: SignTransactionFn
}) {
  return createSolanaSigner({
    publicKey: new PublicKey(address),
    signTransaction: async (
      transaction: TransactionOrVersionedTransaction,
    ): Promise<TransactionOrVersionedTransaction> => {
      // Classified structurally: this app and the SDK resolve different copies
      // of @solana/web3.js, so `instanceof` never matches a transaction the SDK
      // built and every versioned one would take the legacy branch.
      const isLegacy = isLegacySolanaTransaction(transaction)

      const unsignedBytes = isLegacy
        ? transaction.serialize({
            requireAllSignatures: false,
            verifySignatures: false,
          })
        : transaction.serialize()

      const { signedTransaction } = await signTransaction({
        transaction: unsignedBytes,
        wallet,
        chain,
      })

      if (!isLegacy) {
        return VersionedTransaction.deserialize(
          signedTransaction,
        ) as unknown as TransactionOrVersionedTransaction
      }

      return Transaction.from(
        signedTransaction,
      ) as unknown as TransactionOrVersionedTransaction
    },
  })
}
